import { Chat, type UIMessage } from "@ai-sdk/react";

/**
 * The SDK `Chat` constructor's init shape, taken from the class itself rather
 * than imported from `ai` so it always matches the `ai` instance that
 * `@ai-sdk/react` resolved (hosts with a split `zod` peer get two).
 */
export type CoalescedChatInit<UI_MESSAGE extends UIMessage> =
  ConstructorParameters<typeof Chat<UI_MESSAGE>>[0];

/**
 * Minimum spacing between React notifications while a turn streams
 * (~20 renders/sec). Same cadence the SDK's `experimental_throttle` targeted.
 */
export const DEFAULT_NOTIFY_INTERVAL_MS = 50;

export interface MacrotaskCoalescer {
  /** Drop any pending delivery. Safe to call repeatedly. */
  cancel: () => void;
  /** Request a notification. Many calls collapse into one delivery. */
  notify: () => void;
}

/**
 * Collapse a burst of calls into at most one `fn()` per `intervalMs`, always
 * delivered from a timer (a macrotask) — never synchronously from the caller.
 *
 * Why not a plain throttle: the AI SDK consumes the chat stream through a
 * promise chain, so a buffered burst of chunks is written back-to-back in
 * microtasks. `useChat` notifies React via `useSyncExternalStore`, whose
 * updates are SyncLane and are flushed in a microtask too — so a notification
 * that fires synchronously from a chunk write makes React render the whole
 * transcript *inside the stream's microtask chain*, without ever yielding to
 * the task queue. A wall-clock throttle (the SDK's `throttleit`) lets the next
 * chunk straight through whenever the previous render took longer than the
 * window, which is exactly the heavy-transcript case. React's Default-lane
 * work (every `useEffect` → `setState` the commit queued) can only run from a
 * Scheduler task, so it stays pending for the whole burst, and React counts
 * every sync commit made while it's pending as a nested update. At 51 the next
 * `replaceMessage` throws "Maximum update depth exceeded" (minified #185) into
 * the stream writer; the AI SDK surfaces it as the turn's error and the reply
 * is cut off. Large/long chats hit this on every turn.
 *
 * Delivering from a timer breaks the chain: between two notifications the
 * event loop returns to the task queue, React's Scheduler flushes the pending
 * Default-lane work, and the nested-update counter resets. Chunk writes keep
 * mutating the store at full speed; only the React notification is spaced.
 */
export function createMacrotaskCoalescer(
  fn: () => void,
  intervalMs: number = DEFAULT_NOTIFY_INTERVAL_MS
): MacrotaskCoalescer {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastDeliveredAt = Number.NEGATIVE_INFINITY;

  const deliver = () => {
    timer = null;
    lastDeliveredAt = Date.now();
    fn();
  };

  return {
    notify: () => {
      if (timer !== null) {
        return;
      }
      const wait = Math.max(0, lastDeliveredAt + intervalMs - Date.now());
      timer = setTimeout(deliver, wait);
    },
    cancel: () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export interface CoalescedChatOptions {
  /** Minimum spacing between React notifications. Default 50 ms. */
  notifyIntervalMs?: number;
}

/**
 * The AI SDK's React `Chat` with message-store notifications coalesced via
 * {@link createMacrotaskCoalescer} instead of the SDK's wall-clock throttle.
 *
 * Hand an instance to `useChat({ chat })`. The hook's own `throttle` /
 * `experimental_throttle` options are ignored by design — they are the
 * mechanism this class replaces. Status and error notifications stay
 * synchronous (a handful per turn, and `useChat` refreshes its messages
 * snapshot on `ready`/`error`, so the final transcript is never late).
 *
 * `useChat` does not stop an externally supplied chat on unmount; the owner
 * must call `chat.stop()` itself.
 */
export class CoalescedChat<
  UI_MESSAGE extends UIMessage,
> extends Chat<UI_MESSAGE> {
  constructor(
    init: CoalescedChatInit<UI_MESSAGE>,
    { notifyIntervalMs = DEFAULT_NOTIFY_INTERVAL_MS }: CoalescedChatOptions = {}
  ) {
    super(init);
    // `~registerMessagesCallback` is `@ai-sdk/react`-internal (verified
    // against 4.0.111) and is the key `useChat` subscribes through. If a
    // release renames it, fail loudly here: without this override the
    // #185 fix is gone.
    const register = this["~registerMessagesCallback"];
    if (typeof register !== "function") {
      throw new Error(
        "CoalescedChat: @ai-sdk/react's Chat no longer exposes `~registerMessagesCallback`; this SDK version is unsupported."
      );
    }
    this["~registerMessagesCallback"] = (onChange) => {
      const coalescer = createMacrotaskCoalescer(onChange, notifyIntervalMs);
      const unregister = register(coalescer.notify);
      return () => {
        coalescer.cancel();
        unregister();
      };
    };
  }
}
